/**
 * 탐색(연간시간표 셀 / 프로그램 표 학습내용 → 연간진도표) 진입 대상 행 판정.
 * 순수 함수 + 개발용 타이밍 표식만 둔다(DB·DOM 접근 없음).
 */
import { parseAnnualCellKey } from './annualTimetable';

/** 이번 이동 1건을 식별하는 키. 활성 화면 + URL 대상이 있을 때만 생긴다. */
export function buildAnnualEntryFocusNavKey(
  isActive: boolean,
  locationKey: string | undefined,
  focusAnnualCellKey: string
): string {
  const key = (focusAnnualCellKey || '').trim();
  if (!isActive || !key) return '';
  return `${locationKey || 'default'}|${key}`;
}

/** 아직 대상 행 선택·스크롤을 끝내지 않은 이동인지 */
export function isAnnualEntryFocusPending(navKey: string, doneNavKey: string): boolean {
  return navKey !== '' && navKey !== doneNavKey;
}

export type AnnualEntryFocusMatchedBy = 'cellKey' | 'contentRowId' | 'sameDay' | 'none';

export interface AnnualEntryFocusRowLike {
  annualCellKey: string;
  /** 진도표 행에 실제로 표시 중인 프로그램 내용 행 id(편제·프로젝트·고정) */
  entryContentRowId?: string;
}

export interface AnnualEntryFocusResolution {
  index: number;
  matchedBy: AnnualEntryFocusMatchedBy;
  /** 개발용: 정확히 못 찾았거나 내용이 다를 때 이유 */
  note?: string;
}

/**
 * 탐색 대상 행 찾기 — 우선순위:
 * 1) 같은 시간표 칸 키(클래스|주|요일|교시) — 정확 일치
 * 2) (프로그램 표에서 온 경우) 같은 내용 행 id를 표시 중인 행
 * 3) 같은 클래스·같은 날짜(주+요일)에서 가장 가까운 교시 — 이전 주로는 절대 넘어가지 않음
 * 4) 없음 → 선택 해제
 */
export function resolveAnnualEntryFocusTarget(
  rows: ReadonlyArray<AnnualEntryFocusRowLike>,
  rowIdxByAnnualKey: Readonly<Record<string, number>>,
  target: { annualCellKey: string; contentRowId?: string | null }
): AnnualEntryFocusResolution {
  const key = (target.annualCellKey || '').trim();
  const contentRowId = (target.contentRowId || '').trim();
  const exact = rowIdxByAnnualKey[key];
  if (Number.isInteger(exact) && exact >= 0 && exact < rows.length) {
    const shown = (rows[exact]?.entryContentRowId || '').trim();
    const note =
      contentRowId && shown && shown !== contentRowId ? `content mismatch: shown=${shown} want=${contentRowId}` : undefined;
    return { index: exact, matchedBy: 'cellKey', note };
  }
  if (contentRowId) {
    const byContent = rows.findIndex((r) => (r.entryContentRowId || '').trim() === contentRowId);
    if (byContent >= 0) return { index: byContent, matchedBy: 'contentRowId', note: 'cell key not in table' };
  }
  const parsed = parseAnnualCellKey(key);
  if (parsed) {
    let best = -1;
    let bestDist = Number.POSITIVE_INFINITY;
    rows.forEach((r, idx) => {
      const p = parseAnnualCellKey(r.annualCellKey);
      if (!p) return;
      if (p.gradeId !== parsed.gradeId || p.mondayIso !== parsed.mondayIso || p.weekdayKey !== parsed.weekdayKey) return;
      const dist = Math.abs(p.slotIndex - parsed.slotIndex);
      if (dist < bestDist) {
        bestDist = dist;
        best = idx;
      }
    });
    if (best >= 0) return { index: best, matchedBy: 'sameDay', note: 'cell key not in table (period out of range)' };
  }
  return { index: -1, matchedBy: 'none', note: parsed ? 'date/class not in this table' : 'unparseable key' };
}

/** 프로그램 표 행 → 연간진도표 탐색 경로. 실제 시간표 칸과 짝지어진 행만 대상이 된다. */
export function buildAnnualProgressBrowsePath(input: {
  annualCellKey: string;
  isScheduleAvailable?: boolean;
  termNumber: number | null | undefined;
  gradeId: string;
  contentRowId?: string | null;
}): string | null {
  const key = (input.annualCellKey || '').trim();
  if (!key || key.startsWith('orphan:')) return null;
  if (input.isScheduleAvailable === false) return null;
  const term = input.termNumber === 2 ? 2 : input.termNumber === 1 ? 1 : null;
  if (term == null) return null;
  const params = new URLSearchParams();
  const gid = (input.gradeId || '').trim();
  if (gid) params.set('gradeId', gid);
  params.set('focusAnnualCellKey', key);
  const cid = (input.contentRowId || '').trim();
  if (cid && !cid.startsWith('virtual-')) params.set('focusContentRowId', cid);
  return `/annual-progress-term${term}?${params.toString()}`;
}

/* ---------- 개발용 타이밍(운영 빌드에서는 아무것도 하지 않음) ---------- */
const IS_DEV = process.env.NODE_ENV === 'development';
let lastBrowseClickAt = 0;

/** 탐색 버튼을 누른 순간 기록(개발 모드에서만) */
export function markAnnualEntryFocusClick(): void {
  if (!IS_DEV || typeof performance === 'undefined') return;
  lastBrowseClickAt = performance.now();
}

export function logAnnualEntryFocusResolved(info: {
  activatedAt: number;
  refetch: boolean;
  matchedBy: AnnualEntryFocusMatchedBy | 'timeout';
  annualCellKey: string;
  note?: string;
}): void {
  if (!IS_DEV || typeof performance === 'undefined') return;
  const now = performance.now();
  const sinceClick = lastBrowseClickAt > 0 && now - lastBrowseClickAt < 15000 ? now - lastBrowseClickAt : null;
  lastBrowseClickAt = 0;
  // eslint-disable-next-line no-console
  console.info(
    `[탐색] target resolved in ${Math.round(now - info.activatedAt)}ms` +
      (sinceClick != null ? ` (클릭부터 ${Math.round(sinceClick)}ms)` : '') +
      `, refetch: ${info.refetch ? 'yes' : 'no'}, matched by: ${info.matchedBy}` +
      (info.note ? `, note: ${info.note}` : ''),
    info.annualCellKey
  );
}
